/**
 * NotProvisionedLanding — access-denied landing for authenticated Entra
 * users with no user_tenants membership, no service_engagement, and no
 * matching pending_grant for this tenant (Phase 1.6 scope add per the
 * 2026-05-13 opener amendment).
 *
 * Mounted by the AppShell route guard when whoami.outcome ===
 * 'not_provisioned'. Renders instead of the main shell so the user
 * doesn't see a piecemeal 403-filled resource page.
 *
 * Source signal: Marcia Ake — @peaceagingcare.com Entra member without
 * user_tenants row authenticated successfully but landed on the resource
 * page with "Access denied for this tenant" (broken-shell state). This
 * landing is the clean replacement.
 *
 * Accessibility (hotfix 2026-05-16): inner card carries `role="alert"`.
 * The access-denied state is semantically an alert — important, user-
 * blocking message — and the role announces it to assistive tech as the
 * landing mounts. Symmetric with the page-level access-denied panels at
 * AdminReview.tsx:80 + TeamTab.tsx:117, which use the same role for
 * the same access-denied surface intent.
 */

import { type ReactNode } from 'react';
import { Button } from '@/components/primitives/Button';
import { signOut } from '@/lib/signOut';

export interface NotProvisionedLandingProps {
  /**
   * Email / display name from the SWA client principal. Surfaces "Signed
   * in as <email>" attribution so the user can see which account they're
   * signed into (defensive — helps disambiguate personal vs work).
   */
  principalDisplayName: string | null;
}

export function NotProvisionedLanding({
  principalDisplayName,
}: NotProvisionedLandingProps): ReactNode {
  return (
    <div className="min-h-screen flex items-center justify-center px-3 bg-cream">
      <div
        role="alert"
        className="max-w-md w-full bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-6 text-center"
      >
        <h1 className="font-display text-xl font-semibold text-sage-800 mb-2">
          Your access isn&rsquo;t set up yet.
        </h1>
        <p className="text-sm text-ink mb-2">
          Ask your administrator to add you, then sign in again.
        </p>
        {principalDisplayName && (
          <p className="text-xs text-ink-soft mb-6 break-all">
            Signed in as {principalDisplayName}
          </p>
        )}
        <Button variant="primary" onClick={() => void signOut('chosen')}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
